import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Heart, FileText, Calendar, Pill, Smartphone, PenLine, Filter } from 'lucide-react';
import GlassCard from '@/components/ui/glass-card';
import type { LucideIcon } from 'lucide-react';

type TimelineEventType = 'vital' | 'test' | 'appointment' | 'medication' | 'wearable' | 'note';

interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  title: string;
  description: string;
  date: string;
  icon: LucideIcon;
  severity?: 'info' | 'warning' | 'success' | 'error';
  metadata?: Record<string, string>;
}

const mockEvents: TimelineEvent[] = [
  {
    id: '1',
    type: 'wearable',
    title: 'Fitbit: 10,234 steps recorded',
    description: 'Daily goal achieved with 2.3km above target',
    date: new Date().toISOString(),
    icon: Smartphone,
    severity: 'success',
  },
  {
    id: '2',
    type: 'vital',
    title: 'Blood Pressure Recorded: 128/82 mmHg',
    description: 'Measured at home with digital monitor',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    icon: Heart,
    severity: 'info',
  },
  {
    id: '3',
    type: 'wearable',
    title: 'Apple Health: Sleep data synced',
    description: '7h 23m sleep with 85% quality score',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    icon: Smartphone,
    severity: 'success',
  },
  {
    id: '4',
    type: 'medication',
    title: 'Metformin 500mg taken',
    description: 'Morning dose as prescribed',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Pill,
    severity: 'info',
  },
  {
    id: '5',
    type: 'note',
    title: 'Personal health note added',
    description: 'Feeling more energetic after dietary changes',
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    icon: PenLine,
    severity: 'info',
  },
  {
    id: '6',
    type: 'vital',
    title: 'Weight Recorded: 185 lbs',
    description: 'Down 3 lbs from last month',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Heart,
    severity: 'success',
  },
  {
    id: '7',
    type: 'appointment',
    title: 'Appointment with Dr. Sarah Chen',
    description: 'Quarterly diabetes management checkup - completed',
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Calendar,
    severity: 'success',
  },
  {
    id: '8',
    type: 'test',
    title: 'Lab Results: Hemoglobin A1C - 6.8%',
    description: 'Improved from 7.2% last quarter',
    date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    icon: FileText,
    severity: 'warning',
  },
  {
    id: '9',
    type: 'wearable',
    title: 'Garmin: Workout completed',
    description: '45 min cardio session, 380 calories burned',
    date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Smartphone,
    severity: 'success',
  },
  {
    id: '10',
    type: 'medication',
    title: 'Started Vitamin D 2000IU',
    description: 'New supplement added per physician recommendation',
    date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Pill,
    severity: 'info',
  },
  {
    id: '11',
    type: 'vital',
    title: 'Blood Glucose: 142 mg/dL (fasting)',
    description: 'Slightly elevated, review diet plan',
    date: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Heart,
    severity: 'warning',
  },
  {
    id: '12',
    type: 'test',
    title: 'Lipid Panel Results',
    description: 'Total Cholesterol: 195 mg/dL, LDL: 118 mg/dL, HDL: 52 mg/dL',
    date: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
    icon: FileText,
    severity: 'info',
  },
  {
    id: '13',
    type: 'appointment',
    title: 'Cardiology Consultation',
    description: 'Annual heart health assessment - no concerns',
    date: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Calendar,
    severity: 'success',
  },
  {
    id: '14',
    type: 'vital',
    title: 'Blood Pressure: 135/88 mmHg',
    description: 'Clinic reading during checkup',
    date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Heart,
    severity: 'warning',
  },
  {
    id: '15',
    type: 'medication',
    title: 'Lisinopril dosage adjusted',
    description: 'Increased from 10mg to 20mg daily',
    date: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Pill,
    severity: 'info',
  },
  {
    id: '16',
    type: 'wearable',
    title: 'Oura Ring: Low readiness score',
    description: 'Score 62/100 - recommended rest day',
    date: new Date(Date.now() - 56 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Smartphone,
    severity: 'warning',
  },
  {
    id: '17',
    type: 'test',
    title: 'Thyroid Function Test',
    description: 'TSH: 2.1 mIU/L - within normal range',
    date: new Date(Date.now() - 63 * 24 * 60 * 60 * 1000).toISOString(),
    icon: FileText,
    severity: 'success',
  },
  {
    id: '18',
    type: 'note',
    title: 'Dietary log entry',
    description: 'Started Mediterranean diet plan',
    date: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString(),
    icon: PenLine,
    severity: 'info',
  },
  {
    id: '19',
    type: 'vital',
    title: 'Resting Heart Rate: 68 bpm',
    description: 'Recorded via Apple Watch during sleep',
    date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Heart,
    severity: 'success',
  },
  {
    id: '20',
    type: 'appointment',
    title: 'Eye Exam with Dr. Michael Roberts',
    description: 'Diabetic retinopathy screening - no changes',
    date: new Date(Date.now() - 84 * 24 * 60 * 60 * 1000).toISOString(),
    icon: Calendar,
    severity: 'success',
  },
];

type FilterType = 'all' | TimelineEventType;

const filters: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'vital', label: 'Vitals' },
  { id: 'test', label: 'Tests' },
  { id: 'appointment', label: 'Appointments' },
  { id: 'medication', label: 'Medications' },
  { id: 'wearable', label: 'Wearables' },
];

const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 172800) return 'Yesterday';
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
  return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
};

const getDateGroup = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return 'This Week';
  if (diffInDays < 30) return 'This Month';
  return 'Older';
};

const severityColors = {
  info: 'bg-blue-500 dark:bg-blue-400',
  warning: 'bg-yellow-500 dark:bg-yellow-400',
  success: 'bg-green-500 dark:bg-green-400',
  error: 'bg-red-500 dark:bg-red-400',
};

const UnifiedHealthTimeline: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const filteredEvents = useMemo(() => {
    if (activeFilter === 'all') return mockEvents;
    return mockEvents.filter(event => event.type === activeFilter);
  }, [activeFilter]);

  const groupedEvents = useMemo(() => {
    const groups: Record<string, TimelineEvent[]> = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'This Month': [],
      'Older': [],
    };

    filteredEvents.forEach(event => {
      const group = getDateGroup(event.date);
      groups[group].push(event);
    });

    return Object.entries(groups).filter(([_, events]) => events.length > 0);
  }, [filteredEvents]);

  return (
    <div>
      {/* Filter Bar */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <div className="flex gap-2">
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap',
                  activeFilter === filter.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-8">
        {groupedEvents.map(([groupName, events]) => (
          <div key={groupName}>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4">
              {groupName}
            </h3>
            <div className="space-y-4">
              {events.map((event, index) => {
                const Icon = event.icon;
                const isLast = index === events.length - 1;

                return (
                  <div key={event.id} className="relative flex gap-4">
                    {/* Timeline Line */}
                    <div className="hidden sm:flex flex-col items-center">
                      <div
                        className={cn(
                          'w-2 h-2 rounded-full ring-4 ring-white dark:ring-gray-900',
                          event.severity ? severityColors[event.severity] : 'bg-gray-400'
                        )}
                      />
                      {!isLast && (
                        <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-2" />
                      )}
                    </div>

                    {/* Event Card */}
                    <div className="flex-1 pb-4">
                      <GlassCard className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                            event.severity === 'success' && 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                            event.severity === 'warning' && 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
                            event.severity === 'error' && 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
                            (!event.severity || event.severity === 'info') && 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          )}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                                {event.title}
                              </h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {getRelativeTime(event.date)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {event.description}
                            </p>
                            {event.metadata && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Object.entries(event.metadata).map(([key, value]) => (
                                  <span
                                    key={key}
                                    className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                                  >
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </GlassCard>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            No events found for this filter.
          </p>
        </div>
      )}
    </div>
  );
};

export default UnifiedHealthTimeline;
